import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getShiftRotations, deleteShiftRotation, batchDeleteShiftRotations } from '../../../services/shiftRotationService';
import { formatDate } from '../../../utils/Utils';

const useShiftRotationApi = (queryParams) => {
  const { t, i18n } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    getShiftRotations(paramsToUse)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const _delete = useCallback(async (ids) => {
    try {
      if (ids.length === 1) {
        await deleteShiftRotation(ids[0]);
      } else {
        await batchDeleteShiftRotations(ids);
      }
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      return false;
    }
  }, []);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'name', label: t('shiftRotation.name'), options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    { name: 'cycleDays', label: t('shiftRotation.cycleDays'), options: { filter: true, sort: false, filterType: 'textField', filterOptions: { defaultOp: 'gte' } } },
    {
      name: 'anchorDate', label: t('shiftRotation.anchorDate'),
      options: {
        filter: false, sort: true,
        customBodyRender: (v) => formatDate(v, i18n.language),
      },
    },
    { name: 'assignedUserCount', label: t('shiftRotation.assignedUserCount'), options: { filter: false, sort: false } },
  ], [t, i18n.language]);

  const objectSchema = useMemo(() => [
    { name: 'name', label: t('shiftRotation.name'), type: 'text', grid: 12, required: true },
    { name: 'cycleDays', label: t('shiftRotation.cycleDays'), type: 'number', grid: 6, required: true },
    { name: 'anchorDate', label: t('shiftRotation.anchorDate'), type: 'date', grid: 6, required: true },
  ], [t]);

  return { objectSchema, columns, data, fetching, pagination, _delete };
};

export default useShiftRotationApi;
